import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Container
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineArrowRight, AiOutlineCode } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1587620962725-abab7fe55159"; // Coding Setup

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * Functions Component
 * * Explains Function Declaration, Definition, and Invocation.
 * Optimized for Junior Developers with clean comments and copy-paste code.
 */
export default function Functions() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI State Management */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ FAB Visibility Logic */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio Controls */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Utility: Copy Code */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Educational Data */
    const functionModules = useMemo(() => [
        {
            title: "01. Function Declaration",
            tag: "Identifier",
            desc: "Declaration is simply giving your function a name so JavaScript knows it exists. Think of it as reserving a spot in memory.",
            code: `function drive();\nfunction calculateDoubleValue();`
        },
        {
            title: "02. Function Definition",
            tag: "Logic Body",
            desc: "The definition contains the actual logic within curly braces {}. This is where the task is performed.",
            code: `function drive() {\n  // The body where the task is performed\n  console.log("Driving...");\n}`
        },
        {
            title: "03. Invocation (Calling)",
            tag: "Execution",
            desc: "A function does nothing until it is 'called' or 'invoked' using parentheses ().",
            code: `drive(); // Executes the function\nconsole.log(drive()); // Executes and logs the result`
        }
    ], []);

    const implementationModules = useMemo(() => [
        {
            title: "Calculation Pattern",
            tag: "Return Values",
            desc: "Functions can take inputs (parameters) and return a specific output (result).",
            code: `/** \n * Multiplies a value by two \n */\nfunction calDoubleValue(x) {\n  return 2 * x;\n}\n\nlet result = calDoubleValue(4); \nconsole.log(result); // Output: 8`
        },
        {
            title: "Sum & Transform",
            tag: "Multi-Param",
            desc: "You can pass multiple arguments to perform more complex logic in a single block.",
            code: `/** \n * Calculates sum then doubles it \n */\nfunction calSummedValue(x, y) {\n  let xySum = x + y;\n  return 2 * xySum;\n}\n\nconsole.log(calSummedValue(4, 6)); // Output: 20`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVASCRIPT MASTERY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Introduction Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                2. Functions & Methods
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("Functions are the building blocks of JavaScript. While the terms Function and Method are often used interchangeably, the distinction lies in scope.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7, mb: 3 }}>
                            Functions are independent blocks of code. When a function belongs to an object, it's called a <b>Method</b>. Mastering the three stages—Declaration, Definition, and Invocation—is the foundation of JavaScript development.
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: "rgba(218, 165, 32, 0.1)", borderRadius: "12px", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                            <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1, color: "goldenrod" }}>
                                <AiOutlineCode /> Roadmap
                            </Typography>
                            <List dense>
                                {['Declaration', 'Definition', 'Invocation'].map((text) => (
                                    <ListItem key={text} sx={{ py: 0 }}>
                                        <ListItemIcon sx={{ minWidth: 30 }}><AiOutlineArrowRight size={14} color="goldenrod" /></ListItemIcon>
                                        <ListItemText primary={text} primaryTypographyProps={{ fontWeight: 600, color: "white" }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Core Concept Cards */}
                {functionModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Listen">
                                        <IconButton onClick={() => handleSpeak(`${item.title}. ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Real World Implementation */}
                <Grid item xs={12}>
                    <Typography variant="h4" sx={{ textAlign: "center", my: 4, fontWeight: 700, color: "goldenrod" }}>
                        Real-World Implementation
                    </Typography>
                </Grid>



                {implementationModules.map((item, i) => (
                    <Grid item xs={12} key={`impl-${i}`}>
                        <SectionCard sx={{ borderLeft: "6px solid crimson" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                                <Typography variant="h6" color="goldenrod">{item.title}</Typography>
                                <Button
                                    size="small"
                                    startIcon={<CodeIcon />}
                                    sx={{ color: "white" }}
                                    onClick={() => handleCopy(item.code, `impl-${i}`)}
                                >
                                    {copiedIndex === `impl-${i}` ? "Copied" : "Copy"}
                                </Button>
                            </Box>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Box sx={{ p: 4, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <Typography variant="h6" gutterBottom>Ready for more?</Typography>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                            Up next: <b>Higher Order Functions</b> and the elegance of <b>Arrow Functions</b>.
                        </Typography>
                        <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                NEXT LESSON: ADVANCED FUNCTIONS
                            </ActionButton>
                        </Link>
                    </Box>
                </Grid>
            </Grid>

            {/* ✅ Floating Controls */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
